<script setup lang="ts">
// TODO: use Product score
import {
  type SurveyResponseTypeRow,
  type ProductTypeRow,
} from '@openesg/supabase/models'

export interface Props {
  surveyResponse: Pick<
    SurveyResponseTypeRow,
    'score_e' | 'score_s' | 'score_g' | 'grade' | 'score'
  >
  isVerified: boolean
  product?: ProductTypeRow
  isPublic?: boolean
  imageUrl?: string | null
  customTitle?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  product: undefined,
  imageUrl: null,
  isPublic: false,
  customTitle: null,
})

const { score } = useScores(props.surveyResponse)
const esgScores = computed(() => {
  return [
    {
      initial: 'e',
      label: 'Environmental',
      score: props.surveyResponse?.score_e,
    },
    {
      initial: 's',
      label: 'Social',
      score: props.surveyResponse?.score_s,
    },
    {
      initial: 'g',
      label: 'Governance',
      score: props.surveyResponse?.score_g,
    },
  ]
})

const imageComponent = computed(() => {
  if (!props.imageUrl) return

  switch (!!props.product) {
    case true:
      return resolveComponent('ProductImage')
    default:
      return resolveComponent('CompanyLogo')
  }
})
</script>
<template lang="pug">
UiCard(
  v-if="surveyResponse"
  :remove-padding="true"
)
  div(class="p-12 md:p-18")
    div(class="flex justify-between items-baseline w-full py-9 mb-9")
      h3(class="text-sm font-bold flex gap-6 min-w-0")
        span(
          v-if="customTitle"
          class="whitespace-nowrap overflow-hidden text-ellipsis pr-24"
        ) {{ customTitle }}
        span(v-else) Sustainability ID
      span(class="flex items-start gap-4 text-xs text-gray-500 shrink-0")
        | Powered&nbsp;by
        img(
          src="/img/openesg.svg"
          alt="OpenESG"
          width="66"
          height="19"
          class="object-fit mt-1"
        )

    div(
      class="grid items-start w-full gap-12 max-w-240 mx-auto"
      :class="{ 'grid-cols-2 !max-w-480': !!imageUrl }"
    )
      Component(
        :is="imageComponent"
        v-if="imageUrl"
        :url="imageUrl"
        class="aspect-[9/8]"
      )

      ResultScoreBadge(
        :is-verified="props.isVerified"
        :is-product="!!props.product"
        :score="score"
        :is-public="isPublic"
      )

    div(v-if="esgScores" class="flex flex-col w-full gap-9 pt-18")
      div(class="flex justify-between items-center")
        h5(class="text-sm font-bold")
          | Score Factors
          span(class="text-xs font-normal text-gray-600") &nbsp;(/100)

        div(v-if="!props.isPublic" class="text-sm font-bold text-gray-600")
          span Total: {{ score }}
          span(class="text-xs font-normal text-gray-600") &nbsp;/100
      div(class="flex w-full gap-9 lg:gap-12 xl:gap-18 items-center")
        resultEsgMeasure(
          v-for="measure in esgScores"
          :measure="measure"
        )
</template>
<style lang="postcss" scoped>
.grade {
  &.grade-A {
    @apply text-grade-a;
  }
  &.grade-B {
    @apply text-grade-b;
  }
  &.grade-C {
    @apply text-grade-c;
  }
  &.grade-D {
    @apply text-grade-d;
  }
  &.grade-E {
    @apply text-grade-e;
  }
}
</style>
